import { AVAILABLE_QUERY_PARAMS, GanttMode, useProductionPlanQueryParams } from '../../hooks';
import { useCallback } from 'react';
import { BatchGanttViewBase, TaskGanttView } from '@nexelem/graphne-api';
import { openAssignWorkerModal } from '../../modals';
import { toggleTaskDetailsDrawer } from '../../../../../pages/generic/Products/components/TaskDetailDrawer/TaskDetailsDrawer';
import { useGanttSimulationContext } from '../../GanttSimulation/hooks/GanttSimulationContext';
import { useSimulationId } from '../../GanttSimulation/hooks/useSimulationId';
import {
  useGanttSimulationFreezeTask,
  useGanttSimulationPublishTask,
} from '../../../../../queryHooks/productionPlanHooks';
import { NodeFlags } from '../mappers';

type UseOnNodeClickProps = {
  node?: TaskGanttView;
  batchNode?: BatchGanttViewBase;
  nodeFlags: NodeFlags;
};

export const useOnNodeClick = ({ node, batchNode, nodeFlags }: UseOnNodeClickProps) => {
  const taskId = batchNode ? batchNode.id : (node?.id ?? 0);
  const { setQueryParams, queryParams } = useProductionPlanQueryParams();
  const { mode } = useGanttSimulationContext();
  const simulation = useSimulationId();
  const freezeTask = useGanttSimulationFreezeTask(simulation.id, taskId);
  const publishTask = useGanttSimulationPublishTask(simulation.id, taskId);

  const toggleDetailsDrawer = useCallback(
    (taskId: number) =>
      toggleTaskDetailsDrawer({
        taskId: taskId.toString(),
      }),
    [],
  );

  const onClick = useCallback(() => {
    if (!node) {
      return;
    }

    if (mode === 'PUBLICATION') {
      publishTask.mutate({
        toPublish: !nodeFlags.toPublish,
        taskId,
        type: batchNode ? 'batch' : 'task',
      });

      return;
    }

    // Simulation view feature, freezing tasks on click
    if (mode === 'FREEZING') {
      freezeTask.mutate({
        freeze: !nodeFlags.frozen,
        taskId,
        type: batchNode ? 'batch' : 'task',
      });

      return;
    }

    if (queryParams.mode === GanttMode.WORKER) {
      openAssignWorkerModal({ node, batchNode });

      return;
    }

    const { id, queueId, taskExecutionId } = node;

    const isOpen = toggleDetailsDrawer(id);
    const getValue = conditionalGetValue(isOpen);

    setQueryParams([
      {
        key: AVAILABLE_QUERY_PARAMS.queueId,
        value: getValue(queueId),
      },
      {
        key: AVAILABLE_QUERY_PARAMS.taskId,
        value: getValue(id),
      },
      {
        key: AVAILABLE_QUERY_PARAMS.taskExecutionId,
        value: getValue(taskExecutionId),
      },
    ]);
  }, [
    queryParams.mode,
    setQueryParams,
    toggleDetailsDrawer,
    mode,
    freezeTask,
    node,
    batchNode,
    publishTask,
    taskId,
    nodeFlags,
  ]);

  return { onNodeClick: onClick, mode, isFreezPending: freezeTask.isPending, isPublishPending: publishTask.isPending };
};

const conditionalGetValue =
  (isOpen: boolean) =>
  <T,>(value: T): T | undefined =>
    isOpen ? value : undefined;
